import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../../utils/apiError';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

interface WorkloadStaff {
  userId: string;
  name?: string;
  email?: string;
  role?: string;
  openTasks?: number;
  activeCases?: number;
  hoursLogged?: number;
  billableHours?: number;
}

const StaffWorkloadPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('this_month');
  const [drilldown, setDrilldown] = useState<WorkloadStaff | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['staff-workload', user?.tenantId, period],
    queryFn: async () => {
      const res = await fetch(`/api/reports/staff-workload?period=${period}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to load workload'));
      return res.json();
    },
  });

  const staff = data?.data?.staff || [];

  const { data: drillCases = [], isLoading: casesLoading } = useQuery({
    queryKey: ['staff-workload-cases', user?.tenantId, drilldown?.userId],
    enabled: Boolean(drilldown?.userId),
    queryFn: async () => {
      const params = new URLSearchParams({
        assignedTo: drilldown!.userId,
        limit: '25',
      });
      const res = await fetch(`/api/cases?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) return [];
      const json = await res.json();
      return json.data?.cases || [];
    },
  });

  const { data: drillTasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['staff-workload-tasks', user?.tenantId, drilldown?.userId],
    enabled: Boolean(drilldown?.userId),
    queryFn: async () => {
      const params = new URLSearchParams({
        assignedTo: drilldown!.userId,
        status: 'pending',
        limit: '25',
      });
      const res = await fetch(`/api/tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) return [];
      const json = await res.json();
      return json.data?.tasks || json.data || [];
    },
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Staff workload
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Open tasks, active cases, and hours logged for the selected period.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Period</InputLabel>
          <Select
            label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="this_month">This month</MenuItem>
            <MenuItem value="last_month">Last month</MenuItem>
            <MenuItem value="this_quarter">This quarter</MenuItem>
            <MenuItem value="last_12_months">Last 12 months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <ListLoadErrorAlert
          resourceLabel="staff workload"
          message="Staff workload couldn’t be loaded right now. Adjust the period or retry."
          onRetry={() => refetch()}
        />
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Staff counted
                  </Typography>
                  <Typography variant="h4">{staff.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Open tasks
                  </Typography>
                  <Typography variant="h4">
                    {staff.reduce((s: number, r: any) => s + (r.openTasks || 0), 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Billable hours
                  </Typography>
                  <Typography variant="h4">
                    {staff
                      .reduce((s: number, r: any) => s + (r.billableHours || 0), 0)
                      .toFixed(1)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Staff</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Open tasks</TableCell>
                  <TableCell align="right">Active cases</TableCell>
                  <TableCell align="right">Hours logged</TableCell>
                  <TableCell align="right">Billable hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.map((row: WorkloadStaff) => (
                  <TableRow
                    key={row.userId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDrilldown(row)}
                  >
                    <TableCell>
                      <Typography variant="body2">{row.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.role}</TableCell>
                    <TableCell align="right">
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tasks?assignedTo=${row.userId}`);
                        }}
                      >
                        {row.openTasks ?? 0}
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/cases?assignedTo=${row.userId}`);
                        }}
                      >
                        {row.activeCases ?? 0}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{row.hoursLogged}</TableCell>
                    <TableCell align="right">{row.billableHours}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog
        open={Boolean(drilldown)}
        onClose={() => setDrilldown(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {drilldown?.name || 'Staff'} — workload detail
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Active cases
          </Typography>
          {casesLoading ? (
            <CircularProgress size={22} sx={{ my: 1 }} />
          ) : drillCases.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No active cases assigned.
            </Typography>
          ) : (
            <List dense sx={{ mb: 2 }}>
              {drillCases.map((c: any) => (
                <ListItem
                  key={c.id}
                  secondaryAction={
                    <Button
                      size="small"
                      onClick={() => navigate(`/cases/${c.id}/folder`)}
                    >
                      Open
                    </Button>
                  }
                >
                  <ListItemText
                    primary={c.title || c.caseNumber}
                    secondary={c.caseNumber}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" gutterBottom>
            Open / pending tasks
          </Typography>
          {tasksLoading ? (
            <CircularProgress size={22} sx={{ my: 1 }} />
          ) : drillTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pending tasks assigned.
            </Typography>
          ) : (
            <List dense>
              {drillTasks.map((t: any) => (
                <ListItem
                  key={t.id}
                  secondaryAction={
                    t.caseId ? (
                      <Button
                        size="small"
                        onClick={() => navigate(`/cases/${t.caseId}/folder`)}
                      >
                        Case
                      </Button>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={t.title || t.description || 'Task'}
                    secondary={t.status}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`/tasks?assignedTo=${drilldown?.userId}`)}>
            View tasks
          </Button>
          <Button onClick={() => navigate(`/cases?assignedTo=${drilldown?.userId}`)}>
            View cases
          </Button>
          <Button onClick={() => setDrilldown(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffWorkloadPage;
